import { useMemo } from 'react';
import { TrendingUp, Calendar, PoundSterling } from 'lucide-react';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useDashboardSummary } from '@/hooks/useDashboardSummary';
import { format, startOfWeek, addDays } from 'date-fns';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';

interface TimesheetStatsProps {
  onNavigate?: (path: string) => void;
}

export function TimesheetStats({ onNavigate }: TimesheetStatsProps) {
  const { timesheets, currentEmployee, projects } = useTimesheet();
  const { getScreenMode } = useDataSource();
  const { summary } = useDashboardSummary(currentEmployee.id, 'dashboard');
  
  const mode = getScreenMode('dashboard');

  const stats = useMemo(() => {
    // Use live data from backend if available and in live mode
    if (mode === 'live' && summary?.performanceMetrics?.billableRevenue && summary?.performanceMetrics?.thisMonth) {
      return {
        currentWeekHours: summary.performanceMetrics.billableRevenue.totalHours,
        billableHours: summary.performanceMetrics.billableRevenue.billableHours,
        billablePercentage: summary.performanceMetrics.billableRevenue.percentage,
        submittedThisMonth: summary.performanceMetrics.thisMonth.submittedCount,
        avgWeeklyHours: summary.performanceMetrics.avgWeeklyHoursLast4Weeks
      };
    }

    // Fall back to demo data calculation
    const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
    const currentWeekEnd = addDays(currentWeek, 6);

    // Current week timesheet
    const currentTimesheet = timesheets.find(
      ts => ts.employeeId === currentEmployee.id &&
      format(ts.weekEnding, 'yyyy-MM-dd') === format(currentWeekEnd, 'yyyy-MM-dd')
    );

    // Calculate current week hours
    const currentWeekHours = currentTimesheet?.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0) || 0;

    // Calculate billable percentage
    const billableHours = currentTimesheet?.entries.reduce((sum, entry) => {
      const project = projects.find(p => p.id === entry.projectId);
      if (project?.billable) {
        return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
      }
      return sum;
    }, 0) || 0;

    const billablePercentage = currentWeekHours > 0 
      ? Math.round((billableHours / currentWeekHours) * 100) 
      : 0;

    // Count submitted timesheets this month
    const thisMonth = new Date().getMonth();
    const submittedThisMonth = timesheets.filter(
      ts => ts.employeeId === currentEmployee.id &&
      ts.status !== 'draft' &&
      ts.weekEnding.getMonth() === thisMonth
    ).length;

    // Calculate average weekly hours (last 4 weeks)
    const last4Weeks = timesheets
      .filter(ts => ts.employeeId === currentEmployee.id && ts.status !== 'draft')
      .slice(0, 4);

    const avgWeeklyHours = last4Weeks.length > 0
      ? last4Weeks.reduce((sum, ts) => {
          return sum + ts.entries.reduce((entrySum, entry) => {
            return entrySum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
          }, 0);
        }, 0) / last4Weeks.length
      : 0;

    return {
      currentWeekHours,
      billableHours,
      billablePercentage,
      submittedThisMonth,
      avgWeeklyHours
    };
  }, [mode, summary, timesheets, currentEmployee, projects]);

  // Determine performance levels for variant mapping
  const billableVariant = stats.billablePercentage >= 80 ? 'success' : 
                          stats.billablePercentage >= 75 ? 'info' : 
                          stats.billablePercentage >= 60 ? 'warning' : 'error';
  
  const monthlyVariant = stats.submittedThisMonth >= 4 ? 'success' :
                         stats.submittedThisMonth >= 3 ? 'info' : 'warning';
  
  // Avg Weekly is informational only, always use info variant
  const avgWeeklyVariant = 'info';

  // Generate change labels based on performance
  const billableChangeLabel = stats.billablePercentage >= 75 ? 'Above target (75%)' :
                              stats.billablePercentage === 75 ? 'On target (75%)' :
                              'Below target (75%)';

  return (
    <>
      {/* Billable Revenue Card */}
      <NavigationMetricCardAlt
        title="Billable Revenue"
        value={`${stats.billablePercentage}%`}
        icon={PoundSterling}
        variant={billableVariant}
        changeLabel={billableChangeLabel}
        onClick={() => onNavigate?.('/reports/billable')}
        ariaLabel="View billable revenue report"
      />

      {/* This Month Card */}
      <NavigationMetricCardAlt
        title="This Month"
        value={stats.submittedThisMonth.toString()}
        icon={Calendar}
        variant={monthlyVariant}
        changeLabel="Timesheets submitted"
        onClick={() => onNavigate?.('/reports/timesheet')}
        ariaLabel="View monthly timesheet report"
      />

      {/* Avg Weekly Card */}
      <NavigationMetricCardAlt
        title="Avg Weekly"
        value={`${stats.avgWeeklyHours.toFixed(1)}h`}
        icon={TrendingUp}
        variant={avgWeeklyVariant}
        changeLabel="Last 4 weeks average"
        onClick={() => onNavigate?.('/reports/utilization')}
        ariaLabel="View utilization report"
      />
    </>
  );
}
